import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay';
import { brandColors, getContestGenderAccent, getGradeColor } from '@config/brandTokens';
import { agePillButtonSx } from '@components/contest/contestChromeShared';
import { getContestSurvey } from '@config/contestSurveys';
import type { ContestCatalogEntry, ContestGenderKey } from '../../types/contestCatalog';
import {
  getBenchmarkUnit,
  getContestGenderKeys,
  getContestGenderLabel,
  getGradeBandsForAge,
} from '@utils/contestCatalog';

const FRAME_TITLE_MIN_HEIGHT = 52;

/** Seed 2–3 ages around the primary so Compare benchmark opens with content immediately. */
export interface ContestAgeCategoryBarProps {
  sportName: string;
  sportIcon?: React.ReactNode;
  ageCategory: string;
  categories: readonly string[];
  onAgeChange: (age: string) => void;
  trailing?: React.ReactNode;
}

export const ContestAgeCategoryBar: React.FC<ContestAgeCategoryBarProps> = ({
  sportName,
  sportIcon,
  ageCategory,
  categories,
  onAgeChange,
  trailing,
}) => (
  <Box
    sx={{
      px: { xs: 1.25, md: 1.75 },
      py: 1.25,
      bgcolor: brandColors.white,
      borderRadius: 2.5,
      border: '1px solid #e2e8f0',
      boxShadow: '0 1px 4px rgba(17,23,57,0.05)',
      display: 'flex',
      flexDirection: { xs: 'column', lg: 'row' },
      flexWrap: 'wrap',
      alignItems: { xs: 'stretch', lg: 'center' },
      gap: { xs: 1.25, lg: 2 },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
      {sportIcon && (
        <Box sx={{ color: brandColors.navy, display: 'flex', '& .MuiSvgIcon-root': { fontSize: 24 } }}>
          {sportIcon}
        </Box>
      )}
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: { xs: '1.1rem', md: '1.25rem' },
          color: brandColors.navy,
          fontFamily: '"Sora", Arial, sans-serif',
          whiteSpace: 'nowrap',
        }}
      >
        {sportName} Contest
      </Typography>
    </Box>

    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.75,
        flex: 1,
        minWidth: 0,
      }}
    >
      {categories.map((age) => {
        const selected = ageCategory === age;
        return (
          <Button key={age} size="small" onClick={() => onAgeChange(age)} sx={agePillButtonSx(selected)}>
            {age}
          </Button>
        );
      })}
    </Box>

    {trailing}
  </Box>
);

/** Seed 2–3 ages around the primary so Compare benchmark opens with content immediately. */
export function seedCompareAges(primary: string, categories: readonly string[]): string[] {
  const idx = categories.indexOf(primary);
  if (idx < 0) return [primary];
  const picked: string[] = [primary];
  if (idx + 1 < categories.length) picked.push(categories[idx + 1]);
  if (picked.length < 3 && idx - 1 >= 0) picked.unshift(categories[idx - 1]);
  if (picked.length < 3 && idx + 2 < categories.length) picked.push(categories[idx + 2]);
  return Array.from(new Set(picked)).slice(0, 3);
}

/** Compare benchmark / Compare videos + Survey actions for the Learn tab age bar. */
export const ContestLearnAgeActions: React.FC<{
  compareMode: boolean;
  surveyOpen: boolean;
  onCompareModeChange: (enabled: boolean) => void;
  onSurveyOpenChange: (open: boolean) => void;
  showSurvey?: boolean;
  /** Opens the multi-age video compare popup (Learn only; not tied to compareMode). */
  showCompareVideos?: boolean;
  onCompareVideosClick?: () => void;
}> = ({
  compareMode,
  surveyOpen,
  onCompareModeChange,
  onSurveyOpenChange,
  showSurvey = true,
  showCompareVideos = false,
  onCompareVideosClick,
}) => (
  <Stack direction="row" spacing={1} flexShrink={0} alignItems="center" flexWrap="wrap" useFlexGap>
    <Button
      size="small"
      startIcon={<CompareArrowsIcon sx={{ fontSize: 18 }} />}
      onClick={() => {
        const next = !compareMode;
        onCompareModeChange(next);
        if (next) onSurveyOpenChange(false);
      }}
      sx={{
        textTransform: 'none',
        fontWeight: 700,
        borderRadius: '999px',
        px: 1.75,
        color: compareMode ? brandColors.white : brandColors.violet,
        bgcolor: compareMode ? brandColors.violet : '#efe6f8',
        border: `1px solid ${brandColors.violet}`,
        boxShadow: compareMode ? '0 2px 8px rgba(97,22,167,0.22)' : 'none',
        '&:hover': {
          bgcolor: compareMode ? '#4d1290' : '#e8dcf5',
          borderColor: brandColors.violet,
        },
      }}
    >
      Compare benchmark
    </Button>
    {showCompareVideos && onCompareVideosClick && (
      <Button
        size="small"
        startIcon={<SmartDisplayIcon sx={{ fontSize: 18 }} />}
        onClick={() => {
          onSurveyOpenChange(false);
          onCompareVideosClick();
        }}
        sx={{
          textTransform: 'none',
          fontWeight: 700,
          borderRadius: '999px',
          px: 1.75,
          color: brandColors.blue,
          bgcolor: '#e8eefc',
          border: `1px solid ${brandColors.blue}`,
          '&:hover': {
            bgcolor: '#dce6fc',
            borderColor: brandColors.blue,
          },
        }}
      >
        Compare videos
      </Button>
    )}
    {showSurvey && (
    <Button
      size="small"
      startIcon={<AssignmentOutlinedIcon sx={{ fontSize: 18 }} />}
      onClick={() => {
        const next = !surveyOpen;
        onSurveyOpenChange(next);
        if (next) onCompareModeChange(false);
      }}
      sx={{
        textTransform: 'none',
        fontWeight: 700,
        borderRadius: '999px',
        px: 1.75,
        color: surveyOpen ? brandColors.white : brandColors.blue,
        bgcolor: surveyOpen ? brandColors.blue : '#e8eefc',
        border: `1px solid ${brandColors.blue}`,
        boxShadow: surveyOpen ? '0 2px 8px rgba(74,111,235,0.22)' : 'none',
        '&:hover': {
          bgcolor: surveyOpen ? '#3a56c9' : '#dce6fc',
          borderColor: brandColors.blue,
        },
      }}
    >
      Survey
    </Button>
    )}
  </Stack>
);

/** Boy/Girl (or Male/Female) toggle for Self Eval / Contest single-row scorecards. */
export const ContestGenderToggle: React.FC<{
  ageCategory: string;
  value: ContestGenderKey;
  onChange: (gender: ContestGenderKey) => void;
  locked?: boolean;
}> = ({ ageCategory, value, onChange, locked = false }) => (
  <Stack direction="row" spacing={0.75} alignItems="center" flexShrink={0}>
    {getContestGenderKeys(ageCategory).map((key) => {
      const active = value === key;
      const label = getContestGenderLabel(ageCategory, key);
      const accent = getContestGenderAccent(key);
      return (
        <Button
          key={key}
          size="small"
          disabled={locked}
          onClick={() => onChange(key)}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '999px',
            px: 1.5,
            minWidth: 0,
            whiteSpace: 'nowrap',
            color: active ? brandColors.white : accent.accent,
            bgcolor: active ? accent.accent : accent.soft,
            border: `1px solid ${accent.accent}`,
            '&:hover': {
              bgcolor: active ? accent.hover : accent.soft,
            },
            '&.Mui-disabled': {
              color: active ? brandColors.white : undefined,
              bgcolor: active ? accent.accent : undefined,
              opacity: 0.85,
            },
          }}
        >
          {label}
        </Button>
      );
    })}
  </Stack>
);

/** Sport context + age pills + optional benchmark compare (same card). */
export interface ContestSportContextBarProps {
  sportName: string;
  sportIcon?: React.ReactNode;
  ageCategory: string;
  categories: readonly string[];
  onAgeChange: (age: string) => void;
  compareMode: boolean;
  onCompareModeChange: (enabled: boolean) => void;
  compareAges: string[];
  onCompareAgesChange: (ages: string[]) => void;
  /** Selected skill — compare table renders under age pills in this same card */
  selectedContest?: ContestCatalogEntry | null;
}

export const ContestSportContextBar: React.FC<ContestSportContextBarProps> = ({
  sportName,
  sportIcon,
  ageCategory,
  categories,
  onAgeChange,
  compareMode,
  onCompareModeChange,
  compareAges,
  onCompareAgesChange,
  selectedContest = null,
}) => {
  const [surveyOpen, setSurveyOpen] = useState(false);
  const survey = getContestSurvey(sportName);

  const handleAgeClick = (age: string) => {
    onAgeChange(age);
    if (!compareMode) return;
    if (compareAges.includes(age)) {
      if (compareAges.length > 1) {
        onCompareAgesChange(compareAges.filter((a) => a !== age));
      }
      return;
    }
    if (compareAges.length >= 3) {
      onCompareAgesChange([...compareAges.slice(1), age]);
      return;
    }
    onCompareAgesChange([...compareAges, age]);
  };

  return (
    <Box
      sx={{
        px: { xs: 1.25, md: 1.75 },
        py: 1.25,
        bgcolor: brandColors.white,
        borderRadius: 2.5,
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 4px rgba(17,23,57,0.05)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          flexWrap: 'wrap',
          alignItems: { xs: 'stretch', lg: 'center' },
          gap: { xs: 1.25, lg: 2 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {sportIcon && (
            <Box sx={{ color: brandColors.navy, display: 'flex', '& .MuiSvgIcon-root': { fontSize: 24 } }}>
              {sportIcon}
            </Box>
          )}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.1rem', md: '1.25rem' },
              color: brandColors.navy,
              fontFamily: '"Sora", Arial, sans-serif',
              whiteSpace: 'nowrap',
            }}
          >
            {sportName} Contest
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.75,
            flex: 1,
            minWidth: 0,
          }}
        >
          {categories.map((age) => {
            const isPrimary = ageCategory === age;
            const inCompare = compareAges.includes(age);
            const selected = compareMode ? inCompare : isPrimary;
            return (
              <Button
                key={age}
                size="small"
                onClick={() => handleAgeClick(age)}
                sx={agePillButtonSx(selected)}
              >
                {age}
              </Button>
            );
          })}
        </Box>

        <Stack direction="row" spacing={1} flexShrink={0} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            startIcon={<CompareArrowsIcon sx={{ fontSize: 18 }} />}
            onClick={() => {
              const next = !compareMode;
              onCompareModeChange(next);
              if (next) {
                onCompareAgesChange(seedCompareAges(ageCategory, categories));
                setSurveyOpen(false);
              } else {
                onCompareAgesChange([ageCategory]);
              }
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '999px',
              px: 1.75,
              color: compareMode ? brandColors.white : brandColors.navy,
              bgcolor: compareMode ? brandColors.violet : brandColors.ice,
              border: `1px solid ${compareMode ? brandColors.violet : '#d8dee9'}`,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: compareMode ? '#4d1290' : '#efe6f8',
                borderColor: brandColors.violet,
              },
            }}
          >
            Compare benchmark
          </Button>
          <Button
            size="small"
            startIcon={<AssignmentOutlinedIcon sx={{ fontSize: 18 }} />}
            onClick={() => {
              setSurveyOpen((open) => {
                const next = !open;
                if (next && compareMode) {
                  onCompareModeChange(false);
                  onCompareAgesChange([ageCategory]);
                }
                return next;
              });
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '999px',
              px: 1.75,
              color: surveyOpen ? brandColors.white : brandColors.navy,
              bgcolor: surveyOpen ? brandColors.blue : brandColors.ice,
              border: `1px solid ${surveyOpen ? brandColors.blue : '#d8dee9'}`,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: surveyOpen ? '#3a56c9' : '#e8eefc',
                borderColor: brandColors.blue,
              },
            }}
          >
            Survey
          </Button>
        </Stack>
      </Box>

      {compareMode && (
        <ContestBenchmarkComparePanel
          contest={selectedContest}
          compareAges={compareAges}
          embedded
        />
      )}

      {surveyOpen && <ContestSurveyPanel survey={survey} onClose={() => setSurveyOpen(false)} />}
    </Box>
  );
};

/** Sport-specific contest survey — draft questions until product finalizes. */
export const ContestSurveyPanel: React.FC<{
  survey: ReturnType<typeof getContestSurvey>;
  onClose: () => void;
}> = ({ survey, onClose }) => {
  const [answers, setAnswers] = useState<Record<string, string>>({});

  return (
    <Box
      sx={{
        pt: 1,
        borderTop: '1px solid #e8ecf2',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography
          sx={{
            fontWeight: 700,
            color: brandColors.navy,
            fontSize: '0.95rem',
            fontFamily: '"Sora", Arial, sans-serif',
          }}
        >
          {survey.title}
        </Typography>
        <Chip
          size="small"
          label={survey.status === 'draft' ? 'Draft — questions not final' : 'Open'}
          sx={{
            fontWeight: 700,
            bgcolor: survey.status === 'draft' ? '#FFF4E5' : '#ECF8E8',
            color: survey.status === 'draft' ? '#C26A1A' : '#2F7A1F',
          }}
        />
      </Stack>
      <Typography variant="body2" sx={{ color: brandColors.slate, fontSize: '0.85rem' }}>
        {survey.intro}
      </Typography>
      <Alert severity="info" sx={{ py: 0.5 }}>
        Survey responses are not saved yet. Structure is ready for {survey.sport}; final questions will replace this draft.
      </Alert>
      <Stack spacing={1.5}>
        {survey.questions.map((q, index) => (
          <Box key={q.id} sx={{ p: 1.25, borderRadius: 1.5, bgcolor: brandColors.ice, border: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontWeight: 700, color: brandColors.navy, fontSize: '0.88rem', mb: 0.75 }}>
              {index + 1}. {q.prompt}
            </Typography>
            {q.type === 'text' ? (
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                placeholder="Optional"
                value={answers[q.id] || ''}
                onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
              />
            ) : q.type === 'single' && q.options ? (
              <FormControl>
                <RadioGroup
                  value={answers[q.id] || ''}
                  onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                >
                  {q.options.map((opt) => (
                    <FormControlLabel
                      key={opt}
                      value={opt}
                      control={<Radio size="small" />}
                      label={<Typography sx={{ fontSize: '0.85rem' }}>{opt}</Typography>}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            ) : null}
          </Box>
        ))}
      </Stack>
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Close
        </Button>
        <Button
          variant="contained"
          disabled
          sx={{ textTransform: 'none', fontWeight: 700 }}
          title="Submission enabled when survey questions are finalized"
        >
          Submit survey (coming soon)
        </Button>
      </Stack>
    </Box>
  );
};

/** @deprecated Use ContestFrameShell integratedTitle — kept for reference only. */
export const ContestAlignedFrameTitle: React.FC<{
  label: string;
  accent?: string;
  icon?: React.ReactNode;
  variant?: 'primary' | 'secondary';
}> = ({ label, accent = brandColors.navy, icon, variant = 'secondary' }) => (
  <Box
    sx={{
      minHeight: FRAME_TITLE_MIN_HEIGHT,
      px: 1.5,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      bgcolor: variant === 'primary' ? brandColors.white : brandColors.white,
      borderBottom: `2px solid ${accent}`,
      borderRadius: '12px 12px 0 0',
      border: `1px solid #e2e8f0`,
      borderBottomWidth: 2,
      borderBottomColor: accent,
    }}
  >
    {icon}
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: { xs: '0.88rem', md: '0.95rem' },
        color: brandColors.navy,
        fontFamily: '"Sora", Arial, sans-serif',
        lineHeight: 1.25,
      }}
    >
      {label}
    </Typography>
  </Box>
);

/** Side-by-side C / B / A benchmarks for 2–3 age groups on the selected skill. */
export const ContestBenchmarkComparePanel: React.FC<{
  contest: ContestCatalogEntry | null | undefined;
  compareAges: string[];
  /** When true, sits inside the age-group card (no extra outer card chrome). */
  embedded?: boolean;
}> = ({ contest, compareAges, embedded = false }) => {
  if (compareAges.length === 0) return null;

  const compareGradeCodes = compareAges.length
    ? getGradeBandsForAge(contest!, compareAges[0]).map((g) => String(g.code))
    : [];

  const gradeHeader = (code: string) => {
    const c = getGradeColor(code);
    return (
      <Box
        component="th"
        key={code}
        sx={{
          bgcolor: c.bg,
          color: c.text,
          borderBottom: `2px solid ${c.border} !important`,
        }}
      >
        {code}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        pt: embedded ? 1 : 0,
        mt: embedded ? 0 : undefined,
        px: embedded ? 0 : 1.25,
        py: embedded ? 0 : 1.25,
        borderRadius: embedded ? 0 : 2,
        bgcolor: embedded ? 'transparent' : brandColors.white,
        border: embedded ? 'none' : `1px solid ${brandColors.violet}44`,
        borderTop: embedded ? '1px solid #e8ecf2' : undefined,
        boxShadow: embedded ? 'none' : '0 2px 8px rgba(97,22,167,0.08)',
        flexShrink: 0,
      }}
    >
      <Typography
        sx={{
          display: 'block',
          fontWeight: 700,
          color: brandColors.navy,
          mb: 1,
          fontSize: '0.88rem',
          fontFamily: '"Sora", Arial, sans-serif',
        }}
      >
        Compare benchmark{contest ? ` — ${contest.title}` : ''}
      </Typography>
      {!contest ? (
        <Typography variant="body2" sx={{ color: brandColors.slate, fontSize: '0.85rem' }}>
          Select a skill in the list below to compare age benchmarks.
        </Typography>
      ) : compareAges.length < 2 ? (
        <Typography variant="body2" sx={{ color: brandColors.slate, fontSize: '0.85rem' }}>
          Tap another age pill above to compare benchmarks side by side.
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Box
            component="table"
            sx={{
              width: '100%',
              minWidth: 280,
              borderCollapse: 'collapse',
              '& th, & td': {
                px: 1,
                py: 0.75,
                fontSize: '0.85rem',
                textAlign: 'center',
                borderBottom: '1px solid #e2e8f0',
              },
              '& th': {
                fontWeight: 700,
              },
              '& td:first-of-type': {
                textAlign: 'left',
                fontWeight: 700,
                color: brandColors.navy,
              },
            }}
          >
            <thead>
              <tr>
                <Box component="th" sx={{ bgcolor: brandColors.ice, color: brandColors.slate, textAlign: 'left' }}>
                  Age
                </Box>
                {compareGradeCodes.map((code) => gradeHeader(code))}
                <Box component="th" sx={{ bgcolor: brandColors.ice, color: brandColors.slate }}>
                  Unit
                </Box>
              </tr>
            </thead>
            <tbody>
              {compareAges.map((age) => {
                const grades = getGradeBandsForAge(contest, age);
                const unit = getBenchmarkUnit(contest, age) || '—';
                const byCode = Object.fromEntries(grades.map((g) => [g.code, g.label]));
                return (
                  <tr key={age}>
                    <td>{age}</td>
                    {compareGradeCodes.map((code) => {
                      const c = getGradeColor(code);
                      return (
                        <Box
                          component="td"
                          key={code}
                          sx={{ bgcolor: c.bg, color: c.text, fontWeight: 700 }}
                        >
                          {byCode[code] ?? '—'}
                        </Box>
                      );
                    })}
                    <td>{unit}</td>
                  </tr>
                );
              })}
            </tbody>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export { FRAME_TITLE_MIN_HEIGHT };
